import { useMemo } from 'react';
import { Flags } from '@hotpot-arcade/fanforge-core';
import { useMediaUrl } from '@hotpot-arcade/fanforge-meta-client';
import { useAnalytics } from '@hotpot-arcade/analytics-client';
import { GameDefinitions, GameDefinitionWithLogo, GameId } from '../types/games';

export function useGameDefinitions(): GameDefinitions {
  const analytics = useAnalytics();

  return useMemo(() => {
    const gameDefinitions = analytics?.getFeatureFlagPayload(Flags.gameDefinitions) as GameDefinitions;
    if (!gameDefinitions) {
      throw new Error(`${Flags.gameDefinitions} feature flag is undefined`);
    }
    return gameDefinitions;
  }, [analytics]);
}

export function useGameIds(): GameId[] {
  const gameDefinitions = useGameDefinitions();
  return useMemo(() => Object.keys(gameDefinitions) as GameId[], [gameDefinitions]);
}

export function useGame(id: GameId): GameDefinitionWithLogo {
  const gameDefinitions = useGameDefinitions();
  const gameDefinition = useMemo(() => {
    const def = gameDefinitions[id];
    if (!def) {
      throw new Error(`Game definition not found for id: ${id}`);
    }

    return def;
  }, [gameDefinitions, id]);

  const logoUrl = useMediaUrl(gameDefinition.metadata.r2ImageKey);

  return useMemo(
    () => ({
      ...gameDefinition,
      metadata: {
        ...gameDefinition.metadata,
        logoUrl,
      },
    }),
    [gameDefinition, logoUrl]
  );
}
